import CryptoJS from 'crypto-js';
import {
  AttachmentContentType,
  SignatureRecord,
} from '../types/attachment.types';

/** 10 MB: the largest file an attachment may hold. */
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/** How many leading bytes `detectContentType` needs. */
export const ATTACHMENT_SNIFF_BYTES = 16;

const ascii = (bytes: Uint8Array, from: number, to: number) =>
  String.fromCharCode(...bytes.subarray(from, to));

const HEIC_BRANDS = new Set([
  'heic',
  'heix',
  'heim',
  'heis',
  'hevc',
  'hevx',
  'mif1',
  'msf1',
]);

const MP4_BRANDS = new Set([
  'M4A ',
  'isom',
  'iso2',
  'iso5',
  'iso6',
  'mp41',
  'mp42',
  'dash',
]);

/** The content types of a voice note (`AttachmentKind.AUDIO`). */
export const AUDIO_CONTENT_TYPES: ReadonlySet<AttachmentContentType> = new Set([
  AttachmentContentType.WEBM_AUDIO,
  AttachmentContentType.OGG_AUDIO,
  AttachmentContentType.MP4_AUDIO,
]);

/**
 * The type a file really is, from its first bytes (magic numbers), or null
 * when it is none of the accepted types. A renamed or relabelled file is
 * caught here, whatever content type the upload claimed.
 */
export const detectContentType = (
  bytes: Uint8Array,
): AttachmentContentType | null => {
  if (ascii(bytes, 0, 5) === '%PDF-') return AttachmentContentType.PDF;
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return AttachmentContentType.JPEG;
  }
  const png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length >= 8 && png.every((b, i) => bytes[i] === b)) {
    return AttachmentContentType.PNG;
  }
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') {
    return AttachmentContentType.WEBP;
  }
  if (ascii(bytes, 4, 8) === 'ftyp') {
    const brand = ascii(bytes, 8, 12);
    if (HEIC_BRANDS.has(brand)) return AttachmentContentType.HEIC;
    // The audio Safari (and Chrome, as MP4) records. The brand does not
    // tell audio from video: a video would only take its size cap.
    if (MP4_BRANDS.has(brand)) return AttachmentContentType.MP4_AUDIO;
    return null;
  }
  // EBML header: WebM (Chrome, Edge, Firefox).
  const ebml = [0x1a, 0x45, 0xdf, 0xa3];
  if (bytes.length >= 4 && ebml.every((b, i) => bytes[i] === b)) {
    return AttachmentContentType.WEBM_AUDIO;
  }
  if (ascii(bytes, 0, 4) === 'OggS') return AttachmentContentType.OGG_AUDIO;
  return null;
};

/** JSON with object keys sorted at every depth: the same content, the same text. */
export const canonicalJson = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map((v) => canonicalJson(v ?? null)).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries
      .map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

/** SHA-256 (hex) of the canonical JSON of what is being signed. */
export const documentHash = (content: unknown): string =>
  CryptoJS.SHA256(canonicalJson(content)).toString(CryptoJS.enc.Hex);

/**
 * True while the signed content and its document version are unchanged.
 * Any change to a signed value voids the signature: the step is signed again.
 */
export const isSignatureCurrent = (
  signature: Pick<SignatureRecord, 'document_hash' | 'document_version'>,
  content: unknown,
  documentVersion: number,
): boolean =>
  signature.document_version === documentVersion &&
  signature.document_hash === documentHash(content);
